import {
  ClinicalCodeListReadDto,
  ClinicalCommentReadDto,
  ClinicalDatasetReadDto,
  ClinicalLibraryReadDto,
  ClinicalMethodReadDto,
  ClinicalValueListReadDto,
  ClinicalWhereClauseReadDto,
} from "functions/api/library/interface";

/**
 * Interface for merged clinical data arrays.
 *
 * @param clinicalDatasets - Array of clinical datasets
 * @param clinicalCodeLists - Array of clinical code lists
 * @param clinicalValueLists - Array of clinical value lists
 * @param clinicalComments - Array of clinical comments
 * @param clinicalMethods - Array of clinical methods
 * @param clinicalWhereClauses - Array of clinical where clauses
 */
export interface ClinicalData {
  clinicalDatasets: ClinicalDatasetReadDto[];
  clinicalCodeLists: ClinicalCodeListReadDto[];
  clinicalValueLists: ClinicalValueListReadDto[];
  clinicalComments: ClinicalCommentReadDto[];
  clinicalMethods: ClinicalMethodReadDto[];
  clinicalWhereClauses: ClinicalWhereClauseReadDto[];
}

/**
 * Builds a merged clinical library view for annotation and lookup purposes with optimised inheritance logic.
 *
 * This function efficiently merges clinical data from parent hierarchical levels with current level data,
 * ensuring that current library items take precedence over parent items with matching OIDs.
 *
 * **Inheritance Rules:**
 * - All clinical data types (datasets, code lists, value lists, comments, methods, where clauses) support inheritance
 * - Current library data overrides parent duplicates based on OID matching
 * - Parent item order is preserved for unique items
 * - Current item order is preserved for overriding items
 * - If no current library is provided, returns only parent data
 * - Uses efficient Set-based deduplication for O(1) lookup performance
 *
 * @param parentClinicalDatasets - Parent datasets available for inheritance from higher hierarchical levels
 * @param parentClinicalCodeLists - Parent code lists available for inheritance from higher hierarchical levels
 * @param parentClinicalValueLists - Parent value lists available for inheritance from higher hierarchical levels
 * @param parentClinicalComments - Parent comments available for inheritance from higher hierarchical levels
 * @param parentClinicalMethods - Parent methods available for inheritance from higher hierarchical levels
 * @param parentClinicalWhereClauses - Parent where clauses available for inheritance from higher hierarchical levels
 * @param currentLibrary - Optional. Current clinical library to merge with parent data. If undefined, returns only parent data
 * @returns Merged clinical data containing all inherited parent data and current library data, with current taking precedence for duplicates
 */
export const getClinicalDataWithInheritance = (
  parentClinicalDatasets: ClinicalDatasetReadDto[],
  parentClinicalCodeLists: ClinicalCodeListReadDto[],
  parentClinicalValueLists: ClinicalValueListReadDto[],
  parentClinicalComments: ClinicalCommentReadDto[],
  parentClinicalMethods: ClinicalMethodReadDto[],
  parentClinicalWhereClauses: ClinicalWhereClauseReadDto[],
  currentLibrary?: ClinicalLibraryReadDto,
): ClinicalData => {
  if (!currentLibrary) {
    return {
      clinicalDatasets: parentClinicalDatasets,
      clinicalCodeLists: parentClinicalCodeLists,
      clinicalValueLists: parentClinicalValueLists,
      clinicalComments: parentClinicalComments,
      clinicalMethods: parentClinicalMethods,
      clinicalWhereClauses: parentClinicalWhereClauses,
    };
  }

  // merge parent data with current data (current takes precedence)
  return {
    clinicalDatasets: mergeWithInheritedClinicalData(
      parentClinicalDatasets,
      currentLibrary.clinicalDatasets,
      (d) => d.oid,
    ),
    clinicalCodeLists: mergeWithInheritedClinicalData(
      parentClinicalCodeLists,
      currentLibrary.clinicalCodeLists,
      (c) => c.oid,
    ),
    clinicalValueLists: mergeWithInheritedClinicalData(
      parentClinicalValueLists,
      currentLibrary.clinicalValueLists,
      (v) => v.oid,
    ),
    clinicalComments: mergeWithInheritedClinicalData(
      parentClinicalComments,
      currentLibrary.clinicalComments,
      (c) => c.oid,
    ),
    clinicalMethods: mergeWithInheritedClinicalData(
      parentClinicalMethods,
      currentLibrary.clinicalMethods,
      (m) => m.oid,
    ),
    clinicalWhereClauses: mergeWithInheritedClinicalData(
      parentClinicalWhereClauses,
      currentLibrary.clinicalWhereClauses,
      (w) => w.oid,
    ),
  };
};

/**
 * Efficiently merges inherited (parent) items with current items, ensuring current items override parent duplicates.
 *
 * This function uses Set-based deduplication for optimal performance when dealing with large datasets.
 * The merging strategy places current items first to ensure they take precedence, followed by non-duplicate
 * parent items, preserving the original order within each group.
 *
 * @param parentItems - Items from parent hierarchical levels to be inherited
 * @param currentItems - Items from current library that take precedence over parent items
 * @param getKey - Function to extract a unique identifier (typically OID) from each item for deduplication
 * @returns Combined array with current items first, followed by inherited parent items with no duplicates based on key
 */
const mergeWithInheritedClinicalData = <T>(
  parentItems: T[],
  currentItems: T[],
  getKey: (item: T) => string | number,
): T[] => {
  const currentItemKeys = new Set<string | number>(currentItems.map(getKey));
  const inherited = parentItems.filter((p) => !currentItemKeys.has(getKey(p)));
  return [...currentItems, ...inherited];
};
